import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { spacing } from '../../../theme';
import { AutocompleteInput } from '../../AutocompleteInput';
import { Chip } from '../../Chip';

interface Props {
	value: string[];
	onChange: (tags: string[]) => unknown;
	suggestions: string[];
}

const isTagInList = (tagToCheck: string, list: string[]) => {
	return list.some((tag) => tag.toLowerCase() === tagToCheck.toLowerCase());
};

export const TagsEditor = ({ value, onChange, suggestions }: Props) => {
	const { t } = useTranslation();
	const handleTagSelect = (toAdd: string) => {
		if (isTagInList(toAdd, value)) {
			return;
		}
		onChange([...value, toAdd]);
	};

	const handleRemoveTag = (toRemove: string) => () => {
		onChange(value.filter((tag) => tag !== toRemove));
	};

	const suggestionsWithoutSelected = useMemo(
		() => suggestions.filter((suggestion) => !isTagInList(suggestion, value)),
		[suggestions, value],
	);

	return (
		<View style={styles.root}>
			<AutocompleteInput
				suggestions={suggestionsWithoutSelected}
				onSelect={handleTagSelect}
				inputProps={{ label: t('tags-editor.label'), placeholder: t('tags-editor.placeholder') }}
				addNewTextResolver={(inputValue) => t('tags-editor.add-tag', { value: inputValue })}
			/>
			<View style={styles.chipsContainer}>
				{value.map((tag) => (
					<Chip key={tag} text={tag} onDeletePress={handleRemoveTag(tag)} />
				))}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		zIndex: 1,
	},
	chipsContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginVertical: spacing(2),
	},
});
